import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingBag, 
  Users, Package, Calendar, ArrowRight, Clock, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminReports = () => {
  const [period, setPeriod] = useState('monthly');
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllAnalytics();
  }, [period]);

  const loadAllAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [sales, inventory, customers, orders] = await Promise.all([
        axios.get(`${API}/admin/analytics/sales?period=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/inventory`, { headers }),
        axios.get(`${API}/admin/analytics/customers`, { headers }),
        axios.get(`${API}/admin/analytics/orders`, { headers })
      ]);

      setSalesData(sales.data);
      setInventoryData(inventory.data);
      setCustomerData(customers.data);
      setOrderData(orders.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    if (num >= 100000) return (num / 100000).toFixed(1) + 'L';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getChangeIndicator = (change) => {
    if (change > 0) {
      return (
        <span className="flex items-center gap-1 text-green-600 text-sm">
          <TrendingUp className="w-4 h-4" />
          +{change}%
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="flex items-center gap-1 text-red-600 text-sm">
          <TrendingDown className="w-4 h-4" />
          {change}%
        </span>
      );
    }
    return <span className="text-sm text-charcoal-light">0%</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-gold" />
            Analytics & Reports
          </h1>
          <p className="text-charcoal-light mt-1">Track your store performance and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
          >
            <option value="daily">Today</option>
            <option value="weekly">Last 7 Days</option>
            <option value="monthly">Last 30 Days</option>
            <option value="yearly">Last Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-6 h-6 text-gold" />
            {getChangeIndicator(salesData?.summary?.revenue_change)}
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">
            {formatCurrency(salesData?.summary?.total_revenue)}
          </p>
          <p className="text-xs text-charcoal-light">Total Revenue</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <ShoppingBag className="w-6 h-6 text-blue-500" />
            {getChangeIndicator(salesData?.summary?.orders_change)}
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">
            {salesData?.summary?.total_orders || 0}
          </p>
          <p className="text-xs text-charcoal-light">Total Orders</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-6 h-6 text-green-500" />
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              +{customerData?.summary?.new_customers_this_month || 0} new
            </span>
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">
            {customerData?.summary?.total_customers || 0}
          </p>
          <p className="text-xs text-charcoal-light">Total Customers</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-6 h-6 text-purple-500" />
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">
            {formatCurrency(salesData?.summary?.average_order_value)}
          </p>
          <p className="text-xs text-charcoal-light">Avg Order Value</p>
        </div>
      </div>

      {/* Sales Trends */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10">
          <h2 className="font-heading text-lg text-charcoal">Sales Trend</h2>
        </div>
        <div className="p-4">
          {salesData?.trends?.length > 0 ? (
            <div className="space-y-4">
              {/* Simple bar chart representation */}
              <div className="flex items-end gap-1 h-40">
                {salesData.trends.slice(-14).map((day, idx) => {
                  const maxRevenue = Math.max(...salesData.trends.map(d => d.revenue));
                  const height = maxRevenue > 0 ? (day.revenue / maxRevenue * 100) : 0;
                  return (
                    <div
                      key={idx}
                      className="flex-1 bg-gold hover:bg-gold-dark transition-colors cursor-pointer group relative"
                      style={{ height: `${Math.max(height, 4)}%` }}
                      title={`${day.date}: ${formatCurrency(day.revenue)} (${day.orders} orders)`}
                    >
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-charcoal text-white text-xs px-2 py-1 whitespace-nowrap z-10">
                        {formatCurrency(day.revenue)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-charcoal-light">
                <span>{salesData.trends[0]?.date}</span>
                <span>{salesData.trends[salesData.trends.length - 1]?.date}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-charcoal-light">
              No sales data available for this period
            </div>
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
            <h2 className="font-heading text-lg text-charcoal">Top Selling Products</h2>
            <Link to="/admin/products" className="text-sm text-gold hover:underline">
              View All
            </Link>
          </div>
          {salesData?.top_products?.length > 0 ? (
            <div className="divide-y divide-charcoal/5">
              {salesData.top_products.slice(0, 5).map((product, idx) => (
                <div key={idx} className="p-3 flex items-center gap-3">
                  <span className="w-6 h-6 bg-beige text-charcoal-light text-sm flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <img
                    src={product.image || ''}
                    alt=""
                    className="w-10 h-10 object-cover bg-beige"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal truncate">{product.name}</p>
                    <p className="text-xs text-charcoal-light">{product.quantity_sold} sold</p>
                  </div>
                  <p className="text-sm font-medium text-gold">{formatCurrency(product.revenue)}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-charcoal-light">
              No product data available
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10">
            <h2 className="font-heading text-lg text-charcoal">Sales by Category</h2>
          </div>
          {salesData?.category_breakdown?.length > 0 ? (
            <div className="p-4 space-y-3">
              {salesData.category_breakdown.slice(0, 6).map((cat, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-charcoal">{cat.category}</span>
                    <span className="text-sm text-charcoal-light">{cat.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-beige overflow-hidden">
                    <div 
                      className="h-full bg-gold"
                      style={{ width: `${cat.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-charcoal-light">
              No category data available
            </div>
          )}
        </div>
      </div>

      {/* Inventory & Order Status */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Inventory Status */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
            <h2 className="font-heading text-lg text-charcoal">Inventory Status</h2>
            <Link to="/admin/products" className="text-sm text-gold hover:underline">
              Manage
            </Link>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-heading text-green-600">{inventoryData?.summary?.in_stock || 0}</p>
                <p className="text-xs text-charcoal-light">In Stock</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-orange-600">{inventoryData?.summary?.low_stock || 0}</p>
                <p className="text-xs text-charcoal-light">Low Stock</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-red-600">{inventoryData?.summary?.out_of_stock || 0}</p>
                <p className="text-xs text-charcoal-light">Out of Stock</p>
              </div>
            </div>
            
            {inventoryData?.low_stock_items?.length > 0 && (
              <div className="border-t border-charcoal/10 pt-4">
                <p className="text-sm text-charcoal-light mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Low Stock Alerts
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {inventoryData.low_stock_items.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <img src={item.image || ''} alt="" className="w-8 h-8 object-cover bg-beige" />
                      <span className="flex-1 truncate text-charcoal">{item.name}</span>
                      <span className="text-orange-600">{item.low_stock_variants} low</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Status */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
            <h2 className="font-heading text-lg text-charcoal">Order Status</h2>
            <Link to="/admin/orders" className="text-sm text-gold hover:underline">
              View All
            </Link>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-beige/50">
                <p className="text-2xl font-heading text-charcoal">{orderData?.summary?.today_orders || 0}</p>
                <p className="text-xs text-charcoal-light">Today</p>
              </div>
              <div className="text-center p-3 bg-beige/50">
                <p className="text-2xl font-heading text-charcoal">{orderData?.summary?.week_orders || 0}</p>
                <p className="text-xs text-charcoal-light">This Week</p>
              </div>
            </div>
            
            <div className="space-y-2">
              {Object.entries(orderData?.status_breakdown || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-charcoal-light">{status.replace('_', ' ')}</span>
                  <span className="text-charcoal font-medium">{count}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-charcoal/10 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-charcoal-light">Fulfillment Rate</p>
                <p className="text-lg font-heading text-green-600">{orderData?.summary?.fulfillment_rate || 0}%</p>
              </div>
              <div>
                <p className="text-xs text-charcoal-light">Avg Processing</p>
                <p className="text-lg font-heading text-charcoal flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {orderData?.summary?.avg_processing_days || 0} days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Analytics */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
          <h2 className="font-heading text-lg text-charcoal">Top Customers</h2>
          <Link to="/admin/customers" className="text-sm text-gold hover:underline">
            View All
          </Link>
        </div>
        {customerData?.top_customers?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-charcoal/10 text-xs text-charcoal-light uppercase tracking-wider">
                  <th className="p-3 text-left">Customer</th>
                  <th className="p-3 text-center">Orders</th>
                  <th className="p-3 text-right">Total Spent</th>
                  <th className="p-3 text-right">Last Order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal/5">
                {customerData.top_customers.slice(0, 5).map((customer, idx) => (
                  <tr key={idx} className="hover:bg-beige/30">
                    <td className="p-3">
                      <p className="text-sm font-medium text-charcoal">{customer.name}</p>
                      <p className="text-xs text-charcoal-light">{customer.email}</p>
                    </td>
                    <td className="p-3 text-center text-charcoal">{customer.total_orders}</td>
                    <td className="p-3 text-right font-medium text-gold">{formatCurrency(customer.total_spent)}</td>
                    <td className="p-3 text-right text-sm text-charcoal-light">
                      {customer.last_order ? new Date(customer.last_order).toLocaleDateString('en-IN') : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-charcoal-light">
            No customer data available
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
